import type { Tariff } from '../types/subscription';

/**
 * How much cheaper a multi-period tariff is than buying the shortest tariff over and over.
 *
 * Computed from `list_amount` — the regular price — on purpose. A referral or deep-link discount
 * only ever touches the first payment and, for `kind=fixed`, takes the same amount off every
 * tariff, so folding it in would make the plan look more (or less) economical than it really is.
 */
export interface TariffSavings {
    /** How many base periods fit into this tariff (3, 6, …). */
    multiplier: number;
    /** What `multiplier` purchases of the base tariff would cost, in minor units. */
    baselineTotal: number;
    /** `baselineTotal` minus this tariff's own list price, in minor units. Always > 0. */
    saved: number;
    /** Saving as a rounded percentage of `baselineTotal`. Always >= 1. */
    percentOff: number;
}

export interface TariffSavingsIndex {
    /** The shortest tariff every other one is compared against, or null when there is nothing to compare. */
    base: Tariff | null;
    /** Keyed by tariff id. A tariff with no real saving is simply absent. */
    savings: Record<string, TariffSavings>;
}

const listPriceOf = (tariff: Tariff) => tariff.list_amount ?? tariff.amount;

export const buildTariffSavings = (tariffs: Tariff[]): TariffSavingsIndex => {
    const comparable = tariffs.filter((tariff) => tariff.period_seconds > 0);

    if (comparable.length < 2) {
        return { base: null, savings: {} };
    }

    const base = comparable.reduce((shortest, tariff) => (
        tariff.period_seconds < shortest.period_seconds ? tariff : shortest
    ));
    const baseList = listPriceOf(base);

    if (baseList <= 0) {
        return { base: null, savings: {} };
    }

    const savings: Record<string, TariffSavings> = {};

    comparable.forEach((tariff) => {
        if (tariff.id === base.id || tariff.currency !== base.currency) {
            return;
        }

        const multiplier = tariff.period_seconds / base.period_seconds;

        // Only whole multiples can be phrased as "N × 1 oy"; anything else would be a made-up
        // comparison.
        if (multiplier < 2 || Math.abs(multiplier - Math.round(multiplier)) > 1e-6) {
            return;
        }

        const rounded = Math.round(multiplier);
        const baselineTotal = baseList * rounded;
        const saved = baselineTotal - listPriceOf(tariff);

        if (saved <= 0) {
            return;
        }

        const percentOff = Math.round((saved * 100) / baselineTotal);

        // No visible difference — the card stays as it was.
        if (percentOff < 1) {
            return;
        }

        savings[tariff.id] = { multiplier: rounded, baselineTotal, saved, percentOff };
    });

    return { base, savings };
};
